'use client';

import { useState, useEffect } from 'react';
import { createClient } from '../lib/supabase/client';
import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { currentHostIsDemoOrStaging } from '../lib/demo-host';
import { isPrivilegedMfaRole } from '@/lib/mfa/enforcement-policy';
import {
  mfaReminderDismissalCookie,
  mfaReminderEnrolHref,
  mfaReminderSessionStorageKey,
  type MfaReminderSeed,
} from '@/lib/shell/mfa-reminder';

const DISABLE_MFA_COMPLIANCE_BANNER =
  process.env.NEXT_PUBLIC_DISABLE_MFA_COMPLIANCE_BANNER === 'true';

// P1-16 (Paul, 4 Nov 2026: "Warn now, enforce later"). The reminder targets the
// same administrator roles the enforcement policy names, so the cohort warned
// now is the cohort enforced later. A dismissal lasts for this browser session
// only; it supersedes the 2026-08-14 "show once total" composition decision.
// This is a warning, not a gate: nothing here changes access.

export function MfaEnforcementBanner({
  suppress = false,
  initialState,
}: {
  suppress?: boolean;
  initialState?: MfaReminderSeed;
}) {
  const user = useResolvedAuthUser();
  const pathname = usePathname();
  const isAdmin = isPrivilegedMfaRole(user?.role);
  const seed = initialState?.userId === user?.id ? initialState : undefined;
  const [factorState, setFactorState] = useState({
    userId: seed?.userId,
    needsMfa: seed?.needsMfa ?? false,
  });
  const [dismissal, setDismissal] = useState({
    userId: seed?.userId,
    dismissed: seed?.dismissed ?? false,
  });
  const needsMfa = factorState.userId === user?.id && factorState.needsMfa;
  const dismissed = dismissal.userId === user?.id && dismissal.dismissed;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Dismissal is per user. A same-shell account switch must re-read the new
  // user's key, never inherit the previous user's in-memory dismissal.
  useEffect(() => {
    if (!mounted) return;
    if (!user?.id) {
      setDismissal({ userId: undefined, dismissed: false });
      return;
    }
    try {
      const saved =
        seed?.dismissed ||
        window.sessionStorage.getItem(mfaReminderSessionStorageKey(user.id)) === 'true';
      setDismissal({ userId: user.id, dismissed: saved });
      if (saved) rememberDismissal(user.id);
    } catch {
      // Storage unavailable — fall back to per-mount dismissal.
      setDismissal({ userId: user.id, dismissed: seed?.dismissed ?? false });
    }
  }, [mounted, user?.id, seed?.dismissed]);

  function rememberDismissal(userId: string) {
    try {
      // No Max-Age/Expires: a session cookie, so the reminder returns next session.
      document.cookie = `${mfaReminderDismissalCookie(userId)}=1; Path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
    } catch {
      // Cookie restrictions must not revoke an existing browser dismissal.
    }
  }

  function dismissForSession() {
    setDismissal({ userId: user?.id, dismissed: true });
    if (user?.id) {
      try {
        window.sessionStorage.setItem(mfaReminderSessionStorageKey(user.id), 'true');
        rememberDismissal(user.id);
      } catch {
        // Storage unavailable — per-mount dismissal is the best we can do.
      }
    }
  }

  // Keyed on the user id as well as the role: a same-role account switch must
  // re-derive MFA status rather than inherit the previous user's result.
  useEffect(() => {
    if (!mounted) return;
    if (!seed) setFactorState({ userId: user?.id, needsMfa: false });
    const suppressed = suppress || DISABLE_MFA_COMPLIANCE_BANNER || currentHostIsDemoOrStaging();
    if (suppressed) return;
    if (!isAdmin || !user?.id) return;
    const userId = user.id;

    let active = true;
    const supabase = createClient();
    supabase.auth.mfa
      .listFactors()
      .then(({ data, error }) => {
        if (active && !error && data) {
          setFactorState({ userId, needsMfa: !data.totp.length });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [mounted, suppress, isAdmin, user?.id, seed]);

  if (!mounted && !seed) return null;
  if (!isAdmin) return null;
  const suppressed =
    suppress ||
    DISABLE_MFA_COMPLIANCE_BANNER ||
    (mounted ? currentHostIsDemoOrStaging() : seed?.hostSuppressed);
  if (suppressed) return null;
  if (!needsMfa || dismissed) return null;

  return (
    <div
      role="status"
      data-testid="mfa-reminder-banner"
      className="bg-status-warning-bg border-b border-status-warning-border px-4 py-3"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-5 w-5 text-status-warning-fg shrink-0"
          aria-hidden="true"
        >
          <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </svg>
        <p className="flex-1 text-body text-status-warning-fg">
          <strong>Turn on two-factor sign-in.</strong> Administrator accounts can reach client
          health information, and two-factor sign-in will become required for administrators. It
          takes about two minutes with an authenticator app.{' '}
          <Link
            href={mfaReminderEnrolHref(pathname)}
            className="font-medium text-status-warning-fg underline hover:text-amber-700"
          >
            Set up two-factor now
          </Link>
        </p>
        <button
          type="button"
          onClick={dismissForSession}
          className="shrink-0 rounded-control p-1 text-status-warning-fg hover:bg-status-warning-bg hover:text-amber-800"
          aria-label="Hide the two-factor reminder for this session"
          title="Hide for this session"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
